import { useCanvasBackground } from "@/hooks/use-canvas-background";
import { useThree } from "@react-three/fiber";
import { useEffect } from "react";
import { Color } from "three";

type SceneBackgroundProps = {
  /** The background color of the scene */
  color: Color;

  /** Optional callback executed after the background changed */
  onChanged?(): void;
};

/** @returns nothing, sets the scene background color and re-renders the scene */
export function SceneBackground({
  color,
  onChanged,
}: SceneBackgroundProps): null {
  const scene = useThree((s) => s.scene);
  const set = useThree((s) => s.set);
  const invalidate = useThree((s) => s.invalidate);

  // Passive effect so it runs after the Canvas' onCreated, which sets the default background
  useEffect(() => {
    scene.background = color;
    // Mutating the scene doesn't notify the store, so `useThree((s) => s.scene.background)` readers would go stale
    set({ scene });
    onChanged?.();
    invalidate();
  }, [scene, color, set, onChanged, invalidate]);

  return null;
}

/**
 * @returns The scene background color, e.g. to blend framebuffer passes with it.
 * Falls back to the canvas background, since a View's own scene (e.g. the minimap) only gets its background on the first frame.
 */
export function useSceneBackgroundColor(): Color {
  const sceneBackground = useThree((s) => s.scene.background);
  const canvasBackground = useCanvasBackground();
  return sceneBackground instanceof Color ? sceneBackground : canvasBackground;
}
